// src/pages/Home.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "./Home.css";

import HomeNavbar from "../components/HomeNavbar";
import HomeFooter from "../components/HomeFooter";

import InstallButton from "../components/InstallButton";
import FooterBadges from "../components/FooterBadges";

// ── Animated counter ─────────────────────────────────────────
function Counter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / 60;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{count.toLocaleString()}{suffix}</span>;
}

// ── Floating comment card ─────────────────────────────────────
function FloatingCard({ style, comment, action, delay }) {
  return (
    <div className="floating-card" style={{ ...style, animationDelay: delay }}>
      <div className="fc-comment">"{comment}"</div>
      <div className="fc-action">
        <span className="fc-dot" />
        {action}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: "🧠",
    title: "What Should I Do Next?",
    desc: "CreatorComment reads every comment and gives you 1–3 high-priority actions. No scrolling. No guessing. Just act.",
    accent: "#6f6af8",
  },
  {
    icon: "💡",
    title: "Video Ideas from Comments",
    desc: "Your audience is literally telling you what to make. We extract the most-requested topics so you never run out of ideas.",
    accent: "#6af8c4",
  },
  {
    icon: "💬",
    title: "AI Reply Assistant",
    desc: "Draft thoughtful replies to your top comments instantly. Copy and paste, or send directly via the browser extension.",
    accent: "#f8716a",
  },
  {
    icon: "📌",
    title: "Pin Comment Suggestions",
    desc: "Know exactly which comment to pin for maximum engagement. We analyze tone, relevance, and audience impact.",
    accent: "#f8d76a",
  },
  {
    icon: "🚨",
    title: "Missed Opportunities",
    desc: "Unanswered questions and repeated requests quietly kill your growth. We surface them so you can act before it's too late.",
    accent: "#6af8c4",
  },
  {
    icon: "📜",
    title: "History & Re-analysis",
    desc: "Every analysis is saved. Come back anytime to revisit insights or re-analyze a video with fresh eyes.",
    accent: "#6f6af8",
  },
];

const TESTIMONIALS = [
  {
    name: "Priya Mehta",
    handle: "@priyacodes",
    avatar: "PM",
    text: "I found 14 video ideas in my first analysis. My next 3 videos are already planned because of CreatorComment.",
    subs: "48K subscribers",
  },
  {
    name: "Arjun Sharma",
    handle: "@arjuntech",
    avatar: "AS",
    text: "The reply assistant alone saves me 2 hours a week. My comment engagement rate went up 40% in a month.",
    subs: "112K subscribers",
  },
  {
    name: "Neha Kapoor",
    handle: "@nehacreates",
    avatar: "NK",
    text: "Finally I know what my audience actually wants. Stopped guessing, started growing.",
    subs: "23K subscribers",
  },
];

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}


const isIndianUser = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || "";
  return tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || locale.includes("en-IN");
};

export default function Home() {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleLogin = async () => {
    setAuthLoading(true);
    try {
      await loginWithGoogle();
    } catch (e) {
      console.error(e);
    } finally {
      setAuthLoading(false);
    }
  };

  // const isIndia = false;
  const isIndia = isIndianUser();


  return (
    <div className="home">

      {/* ══════════════ NAVBAR ══════════════ */}
      <HomeNavbar />

      {/* ══════════════ HERO ══════════════ */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb orb-a" />
          <div className="hero-orb orb-b" />
          <div className="hero-orb orb-c" />
          <div className="hero-grid" />
        </div>

        <div className="hero-inner">
          <div className="hero-badge">
            <span className="live-dot" />
            AI-powered · Built for YouTube Creators
          </div>

          <h1 className="hero-headline">
            Turn your comments
            <br />
            <span className="headline-accent">into your strategy</span>
          </h1>

          <p className="hero-sub">
            CreatorComment reads every comment on your video and tells you exactly
            what to do next — ideas, replies, opportunities. In seconds.
          </p>

          <div className="hero-cta-group">
            <button className="btn-google-hero" onClick={handleLogin} disabled={authLoading}>
              <GoogleIcon />
              {authLoading ? "Signing in..." : "Start Free with Google"}
            </button>
            <div className="hero-cta-note">No credit card · 5 free analyses/week</div>
          </div>

          {/* Floating UI cards */}
          <div className="hero-cards">
            <FloatingCard
              delay="0s"
              style={{ top: "10%", left: "-33%" }}
              comment="Can you do a video on Next.js 14 app router?"
              action="💡 Added to video ideas"
            />
            <FloatingCard
              delay="0.3s"
              style={{ top: "42%", right: "-35%" }}
              comment="This tutorial saved my project! Thank you!"
              action="📌 Suggested as pin comment"
            />
            <FloatingCard
              delay="0.6s"
              style={{ bottom: "8%", left: "-8%" }}
              comment="What library do you use for animations?"
              action="💬 Reply drafted"
            />
          </div>
        </div>

        {/* Stats bar */}
        <div className="stats-bar">
          {[
            { value: 12400, suffix: "+", label: "Comments analyzed" },
            { value: 3200, suffix: "+", label: "Video ideas generated" },
            { value: 840, suffix: "+", label: "Creators using CreatorComment" },
          ].map(s => (
            <div key={s.label} className="stat-item">
              <div className="stat-num">
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div className="stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ FEATURES ══════════════ */}
      <section className="features-section" id="features">
        <div className="section-inner">
          <div className="section-eyebrow">Everything you need</div>
          <h2 className="section-heading">Stop scrolling. Start growing.</h2>
          <p className="section-sub">
            Every feature is designed around one question: <em>"What should I do next?"</em>
          </p>

          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="feature-card"
                style={{ "--feat-accent": f.accent, animationDelay: `${i * 0.08}s` }}
              >
                <div className="feat-icon" style={{ background: `${f.accent}18`, color: f.accent }}>
                  {f.icon}
                </div>
                <h3 className="feat-title">{f.title}</h3>
                <p className="feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ HOW IT WORKS ══════════════ */}
      <section className="how-section">
        <div className="section-inner">
          <div className="section-eyebrow">How it works</div>
          <h2 className="section-heading">Three steps. Five seconds.</h2>

          <div className="steps-row">
            {[
              { n: "01", title: "Paste a YouTube URL", desc: "Drop any YouTube video link into CreatorComment. We fetch and process all the comments instantly." },
              { n: "02", title: "AI Reads the Room", desc: "Our AI analyzes sentiment, questions, requests, and patterns across thousands of comments in seconds." },
              { n: "03", title: "Get Actionable Insights", desc: "See exactly what to reply, what video to make next, what to pin — no data overload, just clear next steps." },
            ].map((step, i) => (
              <div key={step.n} className="step-card">
                <div className="step-number">{step.n}</div>
                {i < 2 && <div className="step-connector" />}
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ TESTIMONIALS ══════════════ */}
      <section className="testimonials-section" id="testimonials">
        <div className="section-inner">
          <div className="section-eyebrow">Creator love</div>
          <h2 className="section-heading">What creators are saying</h2>

          <div className="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="t-quote">"</div>
                <p className="t-text">{t.text}</p>
                <div className="t-author">
                  <div className="t-avatar">{t.avatar}</div>
                  <div>
                    <div className="t-name">{t.name}</div>
                    <div className="t-handle">{t.handle} · {t.subs}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ PRICING ══════════════ */}
      <section className="pricing-section" id="pricing">
        <div className="section-inner">
          <div className="section-eyebrow">Simple pricing</div>
          <h2 className="section-heading">Pay for value, not tokens</h2>
          <p className="section-sub">Per video analyzed — not per AI call. Transparent. Predictable.</p>

          <div className="pricing-grid">
            {/* Free */}
            <div className="pricing-card">
              <div className="pricing-plan-name">Free</div>
              <div className="pricing-price">
                <span className="pp-amount">{isIndia ? "₹0" : "$0"}</span>
                <span className="pp-period">forever</span>
              </div>
              <ul className="pricing-features">
                <li><span className="pf-check">✓</span> Analyze up to 5 videos/week</li>
                <li><span className="pf-check">✓</span> Limited video ideas</li>
                <li><span className="pf-check">✓</span> Limited AI reply suggestions</li>
                <li><span className="pf-check">✓</span> Pin comment suggestions</li>
                <li><span className="pf-check">✓</span> Limited missed opportunities detection</li>
                <li><span className="pf-check">✓</span> Basic comments explorer</li>
                <li><span className="pf-check">✓</span> History for last 3 videos</li>
                <li><span className="pf-check">✓</span> Standard AI quality</li>
                <li><span className="pf-check">✓</span> Standard processing speed</li>
                <li className="pf-miss"><span className="pf-x">✗</span> Multi-video analysis (Coming Soon)</li>
                <li className="pf-miss"><span className="pf-x">✗</span> Full channel analysis (Coming Soon)</li>
              </ul>
              <button className="btn-pricing ghost" onClick={handleLogin}>
                Start Free
              </button>
            </div>

            {/* Pro */}
            <div className="pricing-card pro">
              <div className="pricing-popular">Most Popular ✦</div>
              <div className="pricing-plan-name">Pro</div>
              <div className="pricing-price">
                <span className="pp-amount">{isIndia ? "₹299" : "$4"}</span>
                <span className="pp-period">/month</span>
              </div>
              <div className="pricing-usd" style={{ marginTop: 4, color: "var(--text-dim)", fontSize: 12 }}>
                {isIndia
                  ? "💰 Or ₹2,999/year — save ₹589 (20% off)"
                  : "💰 Or $39/year — save ~$9 (20% off)"}
              </div>
              <ul className="pricing-features">
                <li><span className="pf-check accent">✓</span> Analyze up to 100 videos/month</li>
                <li><span className="pf-check accent">✓</span> Get 5 AI video ideas per analysis</li>
                <li><span className="pf-check accent">✓</span> Unlimited AI reply drafts</li>
                <li><span className="pf-check accent">✓</span> Pin-worthy comment suggestions</li>
                <li><span className="pf-check accent">✓</span> Missed opportunities detection</li>
                <li><span className="pf-check accent">✓</span> Advanced comment explorer</li>
                <li><span className="pf-check accent">✓</span> Full analysis history</li>
                <li><span className="pf-check accent">✓</span> Multi-video analysis (Coming Soon)</li>
                <li><span className="pf-check accent">✓</span> Full channel analysis (Coming Soon)</li>
                <li><span className="pf-check accent">✓</span> Premium AI quality</li>
                <li><span className="pf-check accent">✓</span> Priority processing speed</li>
              </ul>
              <button className="btn-pricing primary" onClick={handleLogin}>
                Get Pro Access
              </button>
            </div>

            {/* Agency */}
            {/* <div className="pricing-card agency">
              <div className="pricing-plan-name" style={{ color: "var(--text-muted)" }}>Agency</div>
              <div className="pricing-price">
                <span className="pp-amount" style={{ fontSize: 22, color: "var(--text-muted)" }}>Coming Soon</span>
              </div>
              <ul className="pricing-features" style={{ opacity: 0.5 }}>
                <li><span className="pf-check">✓</span> Unlimited videos</li>
                <li><span className="pf-check">✓</span> Multi-channel support</li>
                <li><span className="pf-check">✓</span> Team access</li>
                <li><span className="pf-check">✓</span> Priority support</li>
                <li><span className="pf-check">✓</span> Everything in Pro</li>
              </ul>
              <button className="btn-pricing ghost" disabled style={{ opacity: 0.8 }}>
                Notify Me
              </button>
            </div> */}


          </div>
        </div>
      </section>

      {/* ══════════════ FINAL CTA ══════════════ */}
      <section className="final-cta">
        <div className="final-cta-inner">
          <div className="final-orb" />
          <h2 className="final-headline">
            Your audience is talking.<br />
            Are you listening?
          </h2>
          <p className="final-sub">
            Join hundreds of creators who use CreatorComment to turn comments into content strategy.
          </p>
          <button className="btn-google-hero" onClick={handleLogin} disabled={authLoading} style={{ margin: "0 auto" }}>
            <GoogleIcon />
            {authLoading ? "Signing in..." : "Start Free with Google"}
          </button>
          <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 12 }}>
            Free plan · No credit card · Takes 10 seconds
          </div>
        </div>
      </section>

      <InstallButton/>

      <FooterBadges/>


      {/* ══════════════ FOOTER ══════════════ */}
       <HomeFooter />

    </div>
  );
}